import { React, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./ForgotPassword.css";
import { isNullOrUndefined } from "utils/validationUtil";
import { parseApiError } from "app/services/api.error.parser";
import {
  requestPasswordResetOtp,
  verifyPasswordResetOtp,
  resetPasswordWithToken,
} from "app/services/forgotPassword.service";

import {
  CssBaseline,
  Container,
  Card,
  Grid,
  Button,
  TextField,
  Alert,
  Collapse,
  IconButton,
} from "@mui/material";
import { Close as CloseIcon } from "components/icons";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const PASSWORD_POLICY_REGEX = /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=])(?=\S+$).{8,}$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const RESEND_COOLDOWN_SECONDS = 60;

// Flow steps
const STEP_REQUEST = "request"; // enter email
const STEP_VERIFY = "verify"; // enter OTP
const STEP_RESET = "reset"; // enter new password

export const ForgotPassword = () => {
  const navigate = useNavigate();

  const [step, setStep] = useState(STEP_REQUEST);

  // Data carried across steps (kept in memory only — the reset token never touches the URL).
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const [submitting, setSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Inline alert
  const [message, setMessage] = useState("");
  const [open, setOpen] = useState(false);
  const [severity, setSeverity] = useState("info"); // "error" | "warning" | "info" | "success"

  // Tick down the resend cooldown timer.
  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const showAlert = (msg, sev) => {
    setMessage(msg);
    setSeverity(sev);
    setOpen(true);
  };

  const clearAlert = () => {
    setMessage("");
    setOpen(false);
  };

  // --- Step 1: request OTP ------------------------------------------------
  const handleRequestOtp = async (e) => {
    if (e) e.preventDefault();
    clearAlert();

    if (isNullOrUndefined(email) || email.trim() === "" || !EMAIL_REGEX.test(email.trim())) {
      showAlert("Please enter a valid email address.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      await requestPasswordResetOtp(email.trim());
      // Server is intentionally silent about whether the account exists.
      showAlert(
        "If an account exists for this email, a one-time code has been sent. Please check your inbox.",
        "info"
      );
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setStep(STEP_VERIFY);
    } catch (error) {
      showAlert(parseApiError(error).message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || submitting) return;
    await handleRequestOtp();
  };

  // --- Step 2: verify OTP -------------------------------------------------
  const handleVerifyOtp = async (e) => {
    if (e) e.preventDefault();
    clearAlert();

    if (isNullOrUndefined(otp) || otp.trim() === "") {
      showAlert("Please enter the code sent to your email.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      const response = await verifyPasswordResetOtp(email.trim(), otp.trim());
      setResetToken(response.data.resetToken);
      showAlert("Code verified. Please set your new password.", "success");
      setStep(STEP_RESET);
    } catch (error) {
      showAlert(parseApiError(error).message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  // --- Step 3: reset password ---------------------------------------------
  const handleResetPassword = async (e) => {
    if (e) e.preventDefault();
    clearAlert();

    if (
      isNullOrUndefined(newPassword) ||
      newPassword.trim() === "" ||
      isNullOrUndefined(confirmNewPassword) ||
      confirmNewPassword.trim() === ""
    ) {
      showAlert("All fields are mandatory.", "warning");
      return;
    }

    if (!PASSWORD_POLICY_REGEX.test(newPassword)) {
      showAlert(
        <>
          <p>
            <strong>Password must have</strong>
          </p>
          <ul>
            <li>at least one lowercase letter</li>
            <li>at least one uppercase letter</li>
            <li>at least one digit</li>
            <li>at least one special character (@#$%^&amp;+=)</li>
            <li>no whitespace</li>
            <li>minimum length of 8 characters</li>
          </ul>
        </>,
        "warning"
      );
      return;
    }

    if (newPassword !== confirmNewPassword) {
      showAlert("New password and confirm password do not match.", "warning");
      return;
    }

    setSubmitting(true);
    try {
      await resetPasswordWithToken(resetToken, newPassword, confirmNewPassword);
      showAlert("Password reset successfully. Redirecting to login…", "success");
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      const parsed = parseApiError(error);
      showAlert(parsed.message, "error");
      // An expired/invalid reset token means the OTP session is over — send them back to start.
      if (parsed.status === 400) {
        setTimeout(() => {
          setStep(STEP_REQUEST);
          setOtp("");
          setResetToken("");
        }, 2500);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const headerTitle =
    step === STEP_REQUEST ? "Forgot Password" : step === STEP_VERIFY ? "Enter OTP" : "Reset Password";

  const inlineAlert = (
    <MDBox my={1}>
      <Collapse in={open}>
        <Alert
          action={
            <IconButton aria-label="close" color="inherit" size="small" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          severity={severity}
          sx={{ mb: 2 }}
        >
          {message}
        </Alert>
      </Collapse>
    </MDBox>
  );

  return (
    <Container component="main" maxWidth="xs" className="forgot-password-content-container">
      <CssBaseline />
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                {headerTitle}
              </MDTypography>
            </MDBox>

            <MDBox>
              <div className="forgot-password-wrapper">
                {/* STEP 1 — request OTP */}
                {step === STEP_REQUEST && (
                  <form className="forgot-password-form" onSubmit={handleRequestOtp} noValidate>
                    <MDBox m={3} mb={4} width={500}>
                      <MDBox mb={1}>
                        <MDTypography variant="body2" color="text">
                          Enter your account email and we&apos;ll send you a one-time code.
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={2} width={500}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          id="email"
                          label="Email Address"
                          name="email"
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </MDBox>
                      {inlineAlert}
                      <div className="forgot-password-button">
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                          disabled={submitting}
                        >
                          {submitting ? "Sending…" : "Send OTP"}
                        </Button>
                      </div>
                    </MDBox>
                  </form>
                )}

                {/* STEP 2 — verify OTP */}
                {step === STEP_VERIFY && (
                  <form className="forgot-password-form" onSubmit={handleVerifyOtp} noValidate>
                    <MDBox m={3} mb={4} width={500}>
                      <MDBox mb={1}>
                        <MDTypography variant="body2" color="text">
                          Enter the code sent to <strong>{email}</strong>.
                        </MDTypography>
                      </MDBox>
                      <MDBox mb={2} width={500}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          id="otp"
                          label="One-Time Code"
                          name="otp"
                          inputProps={{ inputMode: "numeric", maxLength: 6 }}
                          value={otp}
                          onChange={(e) => setOtp(e.target.value)}
                        />
                      </MDBox>
                      {inlineAlert}
                      <div className="forgot-password-button">
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                          disabled={submitting}
                        >
                          {submitting ? "Verifying…" : "Verify Code"}
                        </Button>
                      </div>
                      <MDBox mt={2} textAlign="center">
                        <Button
                          variant="text"
                          color="info"
                          onClick={handleResendOtp}
                          disabled={resendCooldown > 0 || submitting}
                        >
                          {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
                        </Button>
                      </MDBox>
                    </MDBox>
                  </form>
                )}

                {/* STEP 3 — reset password */}
                {step === STEP_RESET && (
                  <form className="forgot-password-form" onSubmit={handleResetPassword} noValidate>
                    <MDBox m={3} mb={4} width={500}>
                      <MDBox mb={2} width={500}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          id="new-password"
                          label="New Password"
                          type="password"
                          name="new-password"
                          autoComplete="new-password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                        />
                      </MDBox>
                      <MDBox mb={2} width={500}>
                        <TextField
                          variant="outlined"
                          required
                          fullWidth
                          id="confirm-password"
                          label="Confirm New Password"
                          type="password"
                          name="confirm-password"
                          autoComplete="new-password"
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                        />
                      </MDBox>
                      {inlineAlert}
                      <div className="forgot-password-button">
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          color="primary"
                          disabled={submitting}
                        >
                          {submitting ? "Resetting…" : "Reset Password"}
                        </Button>
                      </div>
                    </MDBox>
                  </form>
                )}

                <MDBox mb={3} textAlign="center">
                  <Button variant="text" color="secondary" onClick={() => navigate("/login")}>
                    Back to Login
                  </Button>
                </MDBox>
              </div>
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};
